import React, { useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { useSelector, useDispatch } from 'react-redux';
import { exchangeSpecificStringifiedNumber } from "../../services/exchanges";
import {
  getFormattedPendings,
  getFormattedCompleted
} from '../../services/utilities/apiDataWithFrontendAddedKeys';
import { orderStatusMap } from "../orderStatupMap"
// import useSound from 'use-sound';
// import tick from './tick.mp3';
// import tick1 from './tick1.mp3';
// import { useSnackbar } from "notistack";
import octopusInstance from "../../services/octopus/octopusInstance";
import { Box } from "@mui/material";
import { IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { fetchPositionsAction } from "../../store/actions/portfolio";

const pendingOrderStatuses = [
  // "ACCEPTED",
  "CONFIRMED",
  // "PENDING",
  "OPEN PENDING",
  "MODORDER",
  "CANORDER",
  "NEWORDER",
  "MODCONFIRMED",
  "NOT MODIFIED",
  "MODIFIED",
  // "MODIFY PENDING",
  // "MODIFY VALIDATION PENDING",
  // "CANCEL PENDING",
  // "EXCHANGE_RESPONSE_PENDING",
  "CANREJECTED",
  "MODREJECTED",
  "VALIDATIONPENDING",
  // "VALIDATION PENDING",
  "PARTIALTRADE",
  "PARTIAL TRADE",
  "PARTIAL_TRADE",
  "AMO_REQ_RECEIVED",
  "AMO_REQ_CONFIRMED",
  "AMO_REQ_MODIFIED",
  "AMO_NEW_CONFIRMED",
  "AMO_MODIFY_CONFIRMED",
  "OPEN",
  "TRIGGER PENDING",
  "AFTER MARKET ORDER REQ RECEIVED",
  "MODIFY AFTER MARKET ORDER REQ RECEIVED",
]

const completedOrderStatuses = [
  "COMPLETE",
  "COMPLETED",
  "REJECTED",
  "CANCONFIRMED",
  "AMO_CANCEL_CONFIRMED",
  "CANCELLED AFTER MARKET ORDER",
  "CANCELLED",
];

const orderStatusVariantMap = {
  "MODORDER": "success",
  "CANORDER": "success",
  "CONFIRMED": "success",
  "NEWORDER": "success",
  "MODCONFIRMED": "success",
  "MODIFIED": "success",
  "CANREJECTED": "error",
  "MODREJECTED": "error",
  "NOT MODIFIED": "error",
  "PARTIALTRADE": "success",
  "PARTIAL TRADE": "success",
  "PARTIAL_TRADE": "success",
  "AMO_REQ_RECEIVED": "success",
  "AMO_REQ_CONFIRMED": "success",
  "AMO_REQ_MODIFIED": "success",
  "AMO_NEW_CONFIRMED": "success",
  "AMO_MODIFY_CONFIRMED": "success",
  "OPEN": "success",
  "TRIGGER PENDING": "warning",
  "AFTER MARKET ORDER REQ RECEIVED": "success",
  "MODIFY AFTER MARKET ORDER REQ RECEIVED": "success",
  "COMPLETE": "success",
  "COMPLETED": "success",
  "REJECTED": "error",
  "CANCONFIRMED": "success",
  "AMO_CANCEL_CONFIRMED": "success",
  "CANCELLED AFTER MARKET ORDER": "warning",
  "CANCELLED": "warning",
  "VALIDATIONPENDING": "warning",
  "OPEN PENDING": "warning",
};

function useOrderUpdates() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const pendingOrders = useSelector((state) => state.orders.arrPendingsData);
  const completedOrders = useSelector((state) => state.orders.arrCompletedData);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  // const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const handlerRef = useRef(null);
  const setupAttempts = useRef(0);

  const setupHandler = () => {
    try {
      const clientId = localStorage.getItem("login_id");
      const authToken = localStorage.getItem("auth_token");
      
      if (!clientId || !authToken) {
        return;
      }

      // Force close any existing connection
      if (handlerRef.current) {
        handlerRef.current.unsubscribe();
        handlerRef.current = null;
      }

      handlerRef.current = octopusInstance.wsHandler({
        messageType: "OrderUpdate",
        subscriptionLocation: "app",
        payload: {
          clientId: clientId,
        },
      });

      if (handlerRef.current) {
        handlerRef.current.subscribe(({ msg }) => {
          if (!msg.oms_order_id) return;
          
          const status = (msg.order_status || '').toUpperCase();
          
          if (pendingOrderStatuses.includes(status)) {
            const order = getFormattedPendings(msg);
            dispatch({
            type: "PREPEND_ORDER_UPDATE",
            payload: order,
            });
            dispatch({
              type: "SET_PENDING_ORDER_UPDATE",
              payload: order,
            });
          } else if (completedOrderStatuses.includes(status)) {
            const order = getFormattedCompleted(msg);
            dispatch({
              type: "REMOVE_PENDING_ORDER",
              payload: order,
            })
            dispatch({
              type: "SET_COMPLETED_ORDER_UPDATE",
              payload: order,
            });
           
          }
        });
      }
    } catch (error) {
      console.error('Handler setup error:', error);
    }
  };

  // Attempt connection when auth state changes
  useEffect(() => {
    if (auth?.isLoggedIn) {
      setupHandler();
    }
  }, [auth?.isLoggedIn]);

  // Retry connection on mount and periodically if needed
  useEffect(() => {
    const attemptConnection = () => {
      const authToken = localStorage.getItem("auth_token");
      const clientId = localStorage.getItem("login_id");
      
      if (authToken && clientId && !handlerRef.current && setupAttempts.current < 3) {
        setupAttempts.current += 1;
        setupHandler();
      }
    };

    // Initial attempt
    attemptConnection();

    // Retry after a short delay
    const retryTimer = setInterval(() => {
      if (!handlerRef.current && setupAttempts.current < 3) {
        attemptConnection();
      }
    }, 1000);

    return () => {
      clearInterval(retryTimer);
      if (handlerRef.current) {
        handlerRef.current.unsubscribe();
        handlerRef.current = null;
      }
    };
  }, []);

  // Reconnect on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setupAttempts.current = 0; // Reset attempts counter
        setupHandler();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return null;
}

export default useOrderUpdates;
